export interface PairedPlaybackState {
  playing: boolean;
  loading: boolean;
  error: boolean;
  currentTime: number;
  duration: number;
}

/** A pair is one transport: buffer, seek, pause and loop at the same timestamp. */
export function createPairedPlayback(
  original: HTMLVideoElement,
  recreated: HTMLVideoElement,
  onChange: (state: PairedPlaybackState) => void,
) {
  const videos = [original, recreated];
  let state: PairedPlaybackState = { playing: false, loading: false, error: false, currentTime: 0, duration: 0 };
  let intent = false;
  let disposed = false;
  let activated = false;
  let attempt: AbortController | null = null;
  const listeners: (() => void)[] = [];
  const update = (patch: Partial<PairedPlaybackState>) => {
    state = { ...state, ...patch };
    if (!disposed) onChange(state);
  };
  const pauseMedia = () => videos.forEach(video => video.pause());
  const duration = () => {
    const values = videos.map(video => video.duration);
    return values.every(value => Number.isFinite(value) && value > 0) ? Math.min(...values) : 0;
  };
  const ready = (video: HTMLVideoElement, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener('canplay', check);
      video.removeEventListener('error', fail);
      signal.removeEventListener('abort', abort);
    };
    const check = () => { if (video.readyState >= 3) { cleanup(); resolve(); } };
    const fail = () => { cleanup(); reject(new Error('PAIRED_VIDEO_UNAVAILABLE')); };
    const abort = () => { cleanup(); reject(new DOMException('Playback cancelled', 'AbortError')); };
    video.addEventListener('canplay', check);
    video.addEventListener('error', fail);
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
    else if (video.error) fail();
    else check();
  });

  const play = async () => {
    if (disposed || state.playing || state.loading) return;
    intent = true;
    attempt?.abort();
    const request = new AbortController();
    attempt = request;
    update({ loading: true, error: false });
    try {
      if (!activated) {
        activated = true;
        videos.forEach(video => { video.preload = 'auto'; video.load(); });
      }
      await Promise.all(videos.map(video => ready(video, request.signal)));
      if (request.signal.aborted || disposed) return;
      const end = duration();
      const position = end && original.currentTime >= end - .04 ? 0 : original.currentTime;
      videos.forEach(video => { if (Math.abs(video.currentTime - position) > .04) video.currentTime = position; });
      await Promise.all(videos.map(video => video.play()));
      if (request.signal.aborted || disposed) {
        if (disposed || !intent) pauseMedia();
        return;
      }
      update({ playing: true, loading: false, duration: end });
    } catch {
      if (request.signal.aborted || disposed) return;
      request.abort(); // Remove the other video's pending ready listeners too.
      intent = false;
      pauseMedia();
      update({ playing: false, loading: false, error: true });
    }
  };
  const pause = () => {
    intent = false;
    attempt?.abort();
    pauseMedia();
    update({ playing: false, loading: false });
  };
  const seek = async (time: number) => {
    const resume = intent;
    pause();
    const position = Math.max(0, Math.min(time, duration()));
    try {
      videos.forEach(video => { video.currentTime = position; });
      update({ currentTime: position });
      if (resume) await play();
    } catch {
      pauseMedia();
      update({ playing: false, loading: false, error: true });
    }
  };
  const listen = (video: HTMLVideoElement, name: string, handler: () => void) => {
    video.addEventListener(name, handler);
    listeners.push(() => video.removeEventListener(name, handler));
  };
  videos.forEach(video => {
    listen(video, 'loadedmetadata', () => update({ duration: duration() }));
    listen(video, 'error', () => {
      attempt?.abort();
      intent = false;
      activated = false;
      pauseMedia();
      update({ playing: false, loading: false, error: true });
    });
    listen(video, 'waiting', async () => {
      if (!intent || state.loading) return;
      pauseMedia();
      update({ playing: false });
      await play();
    });
    listen(video, 'ended', async () => {
      const resume = intent;
      pause();
      videos.forEach(item => { item.currentTime = 0; });
      update({ currentTime: 0 });
      if (resume) await play();
    });
  });
  listen(original, 'timeupdate', () => {
    if (state.playing && Math.abs(recreated.currentTime - original.currentTime) > .12) {
      recreated.currentTime = original.currentTime;
    }
    update({ currentTime: original.currentTime, duration: duration() });
  });
  return {
    play, pause, seek,
    dispose() {
      disposed = true;
      intent = false;
      attempt?.abort();
      listeners.forEach(remove => remove());
      pauseMedia();
    },
  };
}
